import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { ChevronDown, ChevronRight, FileSpreadsheet, Printer, Users as UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default function POByUser() {
  const [pos, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    fetchPOs();
  }, []);

  const fetchPOs = async () => {
    try {
      const res = await api.get('/purchase-orders');
      setPOs(res.data || []);
    } catch (e) {
      toast.error('Gagal memuat data PO');
    } finally {
      setLoading(false);
    }
  };

  const users = useMemo(() => {
    const set = new Set();
    pos.forEach((p) => p.created_by && set.add(p.created_by));
    return Array.from(set).sort();
  }, [pos]);

  const filtered = useMemo(() => {
    return pos.filter((p) => {
      if (selectedUser !== 'all' && p.created_by !== selectedUser) return false;
      if (selectedStatus !== 'all' && p.status !== selectedStatus) return false;
      const d = p.date ? new Date(p.date) : null;
      if (startDate && d && d < new Date(startDate)) return false;
      if (endDate && d && d > new Date(endDate + 'T23:59:59')) return false;
      return true;
    });
  }, [pos, selectedUser, selectedStatus, startDate, endDate]);

  const grouped = useMemo(() => {
    const m = {};
    filtered.forEach((p) => {
      const u = p.created_by || '(unknown)';
      if (!m[u]) m[u] = [];
      m[u].push(p);
    });
    return m;
  }, [filtered]);

  const summary = useMemo(() => {
    const out = [];
    Object.entries(grouped).forEach(([u, list]) => {
      out.push({
        user: u,
        po_count: list.length,
        total_amount: list.reduce((s, p) => s + (p.grand_total || p.total_amount || 0), 0),
        item_count: list.reduce((s, p) => s + (p.items?.length || 0), 0),
      });
    });
    out.sort((a, b) => b.total_amount - a.total_amount);
    return out;
  }, [grouped]);

  const formatRupiah = (v) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(v || 0);

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('id-ID') : '-');

  const toggleExpand = (po) => {
    setExpanded((prev) => ({ ...prev, [po]: !prev[po] }));
  };

  const handleExportExcel = () => {
    if (filtered.length === 0) {
      toast.error('Tidak ada data untuk diexport');
      return;
    }
    // Summary sheet
    const summaryRows = [
      ['PO Report by User'],
      [`Generated: ${new Date().toLocaleString('id-ID')}`],
      [`Filter User: ${selectedUser}`, `Status: ${selectedStatus}`, `From: ${startDate || '-'}`, `To: ${endDate || '-'}`],
      [],
      ['User', 'Total PO', 'Total Items', 'Total Grand Total'],
      ...summary.map((s) => [s.user, s.po_count, s.item_count, s.total_amount]),
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
    wsSummary['!cols'] = [{ wch: 24 }, { wch: 12 }, { wch: 12 }, { wch: 20 }];

    // Detail sheet (one row per item line)
    const detailRows = [
      ['User', 'PO Number', 'Date', 'Supplier', 'Status', 'Item Name', 'Unit', 'Qty', 'Price', 'Amount', 'PO Grand Total'],
    ];
    filtered.forEach((p) => {
      const items = p.items || [];
      if (items.length === 0) {
        detailRows.push([p.created_by, p.po_number, formatDate(p.date), p.supplier_name, p.status, '', '', '', '', '', p.grand_total || p.total_amount || 0]);
      } else {
        items.forEach((it) => {
          detailRows.push([
            p.created_by,
            p.po_number,
            formatDate(p.date),
            p.supplier_name,
            p.status,
            it.item_name,
            it.unit,
            it.quantity,
            it.price,
            it.amount,
            p.grand_total || p.total_amount || 0,
          ]);
        });
      }
    });
    const wsDetail = XLSX.utils.aoa_to_sheet(detailRows);
    wsDetail['!cols'] = [
      { wch: 14 }, { wch: 18 }, { wch: 12 }, { wch: 28 }, { wch: 10 },
      { wch: 32 }, { wch: 8 }, { wch: 10 }, { wch: 14 }, { wch: 16 }, { wch: 18 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
    XLSX.utils.book_append_sheet(wb, wsDetail, 'Detail by Item');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `PO_by_User_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel berhasil diexport');
  };

  const handlePrint = () => {
    if (filtered.length === 0) {
      toast.error('Tidak ada data untuk dicetak');
      return;
    }
    const sumHtml = summary.map((s) => `
      <tr>
        <td>${s.user}</td>
        <td style="text-align:center">${s.po_count}</td>
        <td style="text-align:center">${s.item_count}</td>
        <td style="text-align:right">${formatRupiah(s.total_amount)}</td>
      </tr>`).join('');

    const detailHtml = Object.entries(grouped).map(([u, list]) => {
      const userTotal = list.reduce((s, p) => s + (p.grand_total || p.total_amount || 0), 0);
      const poRows = list.map((p) => {
        const itemRows = (p.items || []).map((it) => `
          <tr class="item-row">
            <td></td>
            <td colspan="2" style="padding-left:20px">${it.item_name}</td>
            <td style="text-align:center">${it.unit}</td>
            <td style="text-align:right">${it.quantity}</td>
            <td style="text-align:right">${formatRupiah(it.price)}</td>
            <td style="text-align:right">${formatRupiah(it.amount)}</td>
          </tr>`).join('');
        return `
          <tr class="po-row">
            <td><b>${p.po_number}</b></td>
            <td>${formatDate(p.date)}</td>
            <td>${p.supplier_name}</td>
            <td style="text-align:center">${p.status}</td>
            <td colspan="2"></td>
            <td style="text-align:right"><b>${formatRupiah(p.grand_total || p.total_amount)}</b></td>
          </tr>
          ${itemRows}
        `;
      }).join('');
      return `
        <h3 style="margin-top:24px;color:#1f2937;border-bottom:2px solid #d1d5db;padding-bottom:4px">
          ${u} <span style="font-weight:normal;font-size:12px;color:#666">(${list.length} PO • ${formatRupiah(userTotal)})</span>
        </h3>
        <table>
          <thead>
            <tr>
              <th>PO Number</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>Status</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Amount / Grand Total</th>
            </tr>
          </thead>
          <tbody>${poRows}</tbody>
        </table>
      `;
    }).join('');

    const html = `<!doctype html><html><head><title>PO Report by User</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        h1{font-size:20px;margin:0 0 4px}
        .meta{font-size:11px;color:#555;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px}
        th,td{border:1px solid #ccc;padding:5px 7px;text-align:left;vertical-align:top}
        thead{background:#f3f4f6}
        .po-row{background:#fafafa;font-weight:500}
        .item-row{font-size:10px;color:#444}
        .summary{margin-bottom:24px}
        @media print{button{display:none}}
      </style></head><body>
      <h1>Purchase Order Report by User</h1>
      <div class="meta">
        User: ${selectedUser} | Status: ${selectedStatus} | From: ${startDate || '-'} | To: ${endDate || '-'} | Printed: ${new Date().toLocaleString('id-ID')}
      </div>
      <h3>Summary</h3>
      <table class="summary">
        <thead><tr><th>User</th><th style="text-align:center">Total PO</th><th style="text-align:center">Total Items</th><th style="text-align:right">Total Grand Total</th></tr></thead>
        <tbody>${sumHtml}</tbody>
      </table>
      ${detailHtml}
      <script>window.onload=()=>{window.print();}</script>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  return (
    <div className="space-y-6 fade-in" data-testid="po-by-user-page">
      <div>
        <h1 className="text-4xl font-bold text-primary mb-2 flex items-center gap-3">
          <UsersIcon className="h-9 w-9" /> PO Report by User
        </h1>
        <p className="text-muted-foreground">Lihat PO yang sudah diinput oleh setiap user, beserta detail item-nya</p>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label>User</Label>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger className="bg-white" data-testid="filter-user-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua User</SelectItem>
                {users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="bg-white" data-testid="filter-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Release">Release</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From Date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="filter-from-date" />
          </div>
          <div>
            <Label>To Date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="filter-to-date" />
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={handleExportExcel} data-testid="export-po-user-excel-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" onClick={handlePrint} data-testid="print-po-user-btn">
              <Printer className="h-4 w-4 mr-1" /> Print
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary */}
      <Card className="p-4">
        <h2 className="text-lg font-semibold mb-3">Summary</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-secondary/50">
                <th className="text-left p-2">User</th>
                <th className="text-center p-2">Total PO</th>
                <th className="text-center p-2">Total Items</th>
                <th className="text-right p-2">Total Grand Total</th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 ? (
                <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Tidak ada data</td></tr>
              ) : summary.map((s) => (
                <tr key={s.user} className="border-b hover:bg-accent/10">
                  <td className="p-2 font-medium">{s.user}</td>
                  <td className="p-2 text-center tabular-nums">{s.po_count}</td>
                  <td className="p-2 text-center tabular-nums">{s.item_count}</td>
                  <td className="p-2 text-right tabular-nums font-semibold">{formatRupiah(s.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail */}
      <Card className="p-4">
        <h2 className="text-lg font-semibold mb-3">Detail PO (Click row to see items)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-secondary/50">
                <th className="w-8 p-2"></th>
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">PO Number</th>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Supplier</th>
                <th className="text-center p-2">Items</th>
                <th className="text-right p-2">Grand Total</th>
                <th className="text-center p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">Tidak ada PO sesuai filter</td></tr>
              ) : filtered.map((p) => {
                const isOpen = !!expanded[p.po_number];
                return (
                  <React.Fragment key={p.po_number}>
                    <tr
                      className="border-b hover:bg-accent/20 cursor-pointer"
                      onClick={() => toggleExpand(p.po_number)}
                      data-testid={`po-row-${p.po_number}`}
                    >
                      <td className="p-2">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                      <td className="p-2 font-medium">{p.created_by || '-'}</td>
                      <td className="p-2 font-mono">{p.po_number}</td>
                      <td className="p-2">{formatDate(p.date)}</td>
                      <td className="p-2">{p.supplier_name}</td>
                      <td className="p-2 text-center">{p.items?.length || 0}</td>
                      <td className="p-2 text-right tabular-nums font-medium">{formatRupiah(p.grand_total || p.total_amount)}</td>
                      <td className="p-2 text-center">
                        <span className={`status-badge ${p.status === 'Release' ? 'status-release' : 'status-open'}`}>{p.status}</span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-secondary/20">
                        <td></td>
                        <td colSpan={7} className="p-3">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-1">Item Name</th>
                                <th className="text-center p-1">Unit</th>
                                <th className="text-right p-1">Qty</th>
                                <th className="text-right p-1">Price</th>
                                <th className="text-right p-1">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(p.items || []).map((it, idx) => (
                                <tr key={idx} className="border-b">
                                  <td className="p-1">{it.item_name}</td>
                                  <td className="p-1 text-center">{it.unit}</td>
                                  <td className="p-1 text-right">{it.quantity}</td>
                                  <td className="p-1 text-right">{formatRupiah(it.price)}</td>
                                  <td className="p-1 text-right">{formatRupiah(it.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
